import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Anchor, Search, Ship, Truck, User, Wrench } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canMaint, isAdmin } from "../lib/roles";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";

const norm = (s) => (s || "").toString().toLowerCase();
const match = (q, ...fields) => fields.some((f) => norm(f).includes(q));

function useStaticData(open, user) {
  const [data, setData] = useState(null);
  const complete = useRef(false);
  useEffect(() => { complete.current = false; setData(null); }, [user?.id]);
  useEffect(() => {
    if (!open || complete.current) { return; }
    let failed = false;
    const get = (ok, path, key) => (ok ? api(path).then((d) => d[key] || []).catch(() => { failed = true; return []; }) : Promise.resolve([]));
    Promise.all([get(isAdmin(user), "/admin/users", "users"), get(canMaint(user), "/maint/impianti", "impianti"), get(canMaint(user), "/maint/beni-mobili", "beni")])
      .then(([users, impianti, beni]) => { complete.current = !failed; setData({ users, impianti, beni }); });
  }, [open, user]);
  return data;
}

function useMarina(open, q) {
  const [res, setRes] = useState({ berths: [], boats: [] });
  useEffect(() => {
    if (!open || q.length < 1) { setRes({ berths: [], boats: [] }); return undefined; }
    const t = setTimeout(() => {
      Promise.all([`/marina/berths?q=${encodeURIComponent(q)}`, `/marina/boats/search?q=${encodeURIComponent(q)}`].map((p) => api(p).then((d) => d.items || []).catch(() => [])))
        .then(([berths, boats]) => setRes({ berths: berths.slice(0, 8), boats: boats.slice(0, 8) }));
    }, 300);
    return () => clearTimeout(t);
  }, [open, q]);
  return res;
}

const Row = ({ icon: Icon, title, sub, onSelect, testid }) => (
  <CommandItem onSelect={onSelect} className="gap-3 py-2.5 cursor-pointer" data-testid={testid}>
    <Icon className="w-4 h-4 text-[var(--teal)] shrink-0" />
    <div className="min-w-0"><p className="font-medium text-[var(--navy)] truncate">{title}</p>{sub && <p className="text-xs text-slate-500 truncate">{sub}</p>}</div>
  </CommandItem>
);

export function GlobalSearch({ open, onOpenChange }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const data = useStaticData(open, user);
  const nq = norm(q.trim());
  const marina = useMarina(open, q.trim());
  const go = (to) => { onOpenChange(false); setQ(""); nav(to); };
  const r = useMemo(() => {
    if (!data || !nq) return { users: [], impianti: [], beni: [] };
    return {
      users: data.users.filter((u) => match(nq, `${u.cognome} ${u.nome}`, `${u.nome} ${u.cognome}`, u.email, u.mansione, ...(u.mansioni || []))).slice(0, 8),
      impianti: data.impianti.filter((i) => match(nq, i.descrizione, i.area_label)).slice(0, 8),
      beni: data.beni.filter((b) => match(nq, b.nome, b.targa, b.tipo, b.tipo_altro)).slice(0, 8),
    };
  }, [data, nq]);
  const total = r.users.length + r.impianti.length + r.beni.length + marina.berths.length + marina.boats.length;
  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) setQ(""); }}>
      <DialogContent className="overflow-hidden p-0 top-[12%] translate-y-0 max-w-[calc(100vw-1.5rem)] sm:max-w-xl rounded-2xl" data-testid="global-search-dialog">
        <DialogTitle className="sr-only">Cerca</DialogTitle>
        <Command shouldFilter={false} className="[&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider">
          <CommandInput value={q} onValueChange={setQ} placeholder="Cerca dipendenti, posti barca, impianti, mezzi…" data-testid="global-search-input" />
          <CommandList className="max-h-[60vh]">
            {nq && total === 0 && <CommandEmpty data-testid="global-search-empty">{data ? "Nessun risultato" : "Caricamento…"}</CommandEmpty>}
            {!nq && <p className="py-6 text-center text-sm text-slate-500" data-testid="global-search-hint">Scrivi un nome, un posto barca (es. P 12), un impianto o una targa</p>}
            {r.users.length > 0 && <CommandGroup heading="Dipendenti">{r.users.map((u) => <Row key={u.id} icon={User} title={`${u.cognome} ${u.nome}`} sub={(u.mansioni?.length ? u.mansioni : [u.mansione]).filter(Boolean).join(" · ")} onSelect={() => go(`/admin/users/${u.id}`)} testid={`search-user-${u.id}`} />)}</CommandGroup>}
            {marina.berths.length > 0 && <CommandGroup heading="Posti barca">{marina.berths.map((b) => <Row key={b.id} icon={Anchor} title={`${b.pontile} - ${b.numero}`} sub={b.occupied ? b.boat_name || "Occupato" : "Libero"} onSelect={() => go(`/marina/berths?open=${b.id}`)} testid={`search-berth-${b.id}`} />)}</CommandGroup>}
            {marina.boats.length > 0 && <CommandGroup heading="Barche">{marina.boats.map((b, i) => <Row key={`${b.berth_id || b.id}-${i}`} icon={Ship} title={b.nome_barca || b.boat_name} sub={`Posto ${b.pontile} - ${b.numero}`} onSelect={() => go(`/marina/berths?open=${b.berth_id || b.id}`)} testid={`search-boat-${b.berth_id || b.id}-${i}`} />)}</CommandGroup>}
            {r.impianti.length > 0 && <CommandGroup heading="Impianti">{r.impianti.map((i) => <Row key={i.id} icon={Wrench} title={i.descrizione} sub={i.area_label} onSelect={() => go(`/manutenzioni/impianti?focus=${i.id}`)} testid={`search-impianto-${i.id}`} />)}</CommandGroup>}
            {r.beni.length > 0 && <CommandGroup heading="Mezzi">{r.beni.map((b) => <Row key={b.id} icon={Truck} title={b.nome} sub={[b.tipo === "altro" ? b.tipo_altro : b.tipo, b.targa].filter(Boolean).join(" · ")} onSelect={() => go(`/manutenzioni/beni-mobili/${b.id}`)} testid={`search-bene-${b.id}`} />)}</CommandGroup>}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

export const SearchTrigger = ({ onClick, className = "", testid }) => (
  <button onClick={onClick} className={className} data-testid={testid} aria-label="Cerca"><Search className="w-[18px] h-[18px]" strokeWidth={1.8} /></button>
);
